import React from "react";
import Container from "./Container";
import Hamburger from "./Hamburger";
import Logo from "./Logo";
import Search from "~/components/Search";
import Cart from "./Cart";
import SignIn from "./SignIn";
import Nav, { NavBackControl, NavCloseControl, NavControls } from "./Nav";
import NavItem, { NavLink } from "./NavItem";
import DownArrow from "./DownArrow";
import { MenuBrands, MenuCategories, MenuCuts, MenuIndustries } from "./Menu";
import MenuItem, {
  MenuBrandLogo,
  MenuBrandItem,
  MenuBrandLink,
  MenuBrandViewAll,
  MenuLink,
} from "./MenuItem";
import { Mill } from "~/types/mill";
import { Cart as TCart } from "~/types/cart";
import { useLocation, useNavigate } from "@remix-run/react";
import useBreakpoints from "~/hooks/useBreakpoints";
import { WindowWithGTM } from "~/types/global";

interface Props {
  cart: TCart | null;
  mills: Promise<Mill[] | null>;
  hasSession: boolean;
}

const HeaderForStore: React.FC<Props> = ({ cart, hasSession, mills }) => {
  const { isDesktop } = useBreakpoints();
  const navigate = useNavigate();
  const { pathname, search, state } = useLocation();
  const [iconDropdownActive, setIconDropdownActive] = React.useState<
    "signin" | "cart" | undefined
  >(undefined);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const [menu, setMenu] = React.useState<
    "categories" | "brands" | "industries" | "cuts" | "all"
  >("all");
  const [resolvedMills, setResolvedMills] = React.useState<Mill[] | null>(null);
  const [pageTitle, setPageTitle] = React.useState<string>("");

  React.useEffect(() => {
    setPageTitle(window.document.title);
  }, [pathname]);

  const handleDTFNavLinkClick = () => {
    const { dataLayer } = window as WindowWithGTM;
    dataLayer?.push({
      event: "navElementClicked",
      navElement: "DTF Transfers",
      pagePath: pathname,
      pageTitle: pageTitle,
    });
  };

  const handleClick = () => {
    setMobileNavOpen(false);
    setIconDropdownActive(undefined);
  };

  React.useEffect(() => {
    if (resolvedMills) return;
    mills.then((resolvedMills) => setResolvedMills(resolvedMills));
  }, [resolvedMills, mills]);

  React.useEffect(() => {
    if (isDesktop) {
      return;
    }

    if (iconDropdownActive || mobileNavOpen) {
      return navigate(pathname + search, { state: { isMobileMenuOpen: true } });
    } else if (state?.isMobileMenuOpen) {
      return navigate(pathname + search, { state: null });
    }
  }, [iconDropdownActive, mobileNavOpen, state?.isMobileMenuOpen]);

  const handleHamburgerClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenu("all");
    setIconDropdownActive(undefined);
    setMobileNavOpen(!mobileNavOpen);
  };

  const handleNavBackClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenu("all");
  };

  const handleNavCloseClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenu("all");
    setMobileNavOpen(false);
  };

  const setIsDropdownActiveFor =
    (value: "signin" | "cart") => (isOpen: boolean) => {
      setMenu("all");
      setMobileNavOpen(false);
      setIconDropdownActive(isOpen ? value : undefined);
    };

  return (
    <Container onClick={handleClick}>
      <Hamburger
        isActive={mobileNavOpen}
        onClick={handleHamburgerClick}
      />
      <Logo />
      <Search className="relative order-4 w-full self-start md:order-none md:w-[50vw] md:max-w-[53%]" />
      <SignIn
        isDropdownActive={iconDropdownActive === "signin"}
        setIsDropdownActive={setIsDropdownActiveFor("signin")}
        hasSession={hasSession}
      />
      <Cart
        isDropdownActive={iconDropdownActive === "cart"}
        setIsDropdownActive={setIsDropdownActiveFor("cart")}
        cart={cart}
      />
      <Nav
        mobileMenu={menu}
        isActive={mobileNavOpen}
      >
        <NavControls>
          {menu !== "all" && <NavBackControl onClick={handleNavBackClick} />}
          <NavCloseControl onClick={handleNavCloseClick} />
        </NavControls>
        <NavItem isActive={menu === "all"}>
          <NavLink href="/t-shirts">T-Shirts</NavLink>
        </NavItem>
        <NavItem isActive={menu === "all"}>
          <NavLink href="/sweatshirts">Sweatshirts</NavLink>
        </NavItem>
        <NavItem isActive={menu === "all"}>
          <NavLink href="/tank-tops-sleeveless-shirts">Tank Tops</NavLink>
        </NavItem>
        <NavItem
          isActive={menu === "all"}
          className="md:hidden lg:flex"
        >
          <NavLink href="/t-shirts/mens">Men</NavLink>
        </NavItem>
        <NavItem
          isActive={menu === "all"}
          className="md:hidden lg:flex"
        >
          <NavLink href="/t-shirts/womens">Women</NavLink>
        </NavItem>
        <NavItem
          isActive={menu === "all"}
          className="md:hidden lg:flex"
        >
          <NavLink href="/t-shirts/unisex">Unisex</NavLink>
        </NavItem>
        <NavItem
          isActive={menu === "all"}
          className="md:hidden lg:flex"
        >
          <NavLink href="/t-shirts/kids">Kids</NavLink>
        </NavItem>
        <NavItem
          isActive={["cuts", "all"].includes(menu)}
          className="md:block lg:hidden"
        >
          {menu === "all" ? (
            <NavLink
              href="#"
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                event.stopPropagation();
                setMenu("cuts");
              }}
              isActive={false}
              isDropdown={true}
            >
              Cut
              <DownArrow />
            </NavLink>
          ) : null}
          <MenuCuts isActive={menu === "cuts"}>
            {[
              { href: "/t-shirts/mens", label: "Men" },
              { href: "/t-shirts/womens", label: "Women" },
              { href: "/t-shirts/unisex", label: "Unisex" },
              { href: "/t-shirts/kids", label: "Kids" },
            ].map(({ href, label }) => (
              <MenuItem key={href}>
                <MenuLink href={href}>{label}</MenuLink>
              </MenuItem>
            ))}
          </MenuCuts>
        </NavItem>
        <NavItem isActive={["categories", "all"].includes(menu)}>
          {menu === "all" ? (
            <NavLink
              href="/categories"
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                event.stopPropagation();
                setMenu("categories");
              }}
              isActive={false}
              isDropdown={true}
            >
              Category
              <DownArrow />
            </NavLink>
          ) : null}
          <MenuCategories isActive={menu === "categories"}>
            {[
              { href: "/accessories", label: "Accessories" },
              { href: "/athletic-apparel", label: "Athletic Apparel" },
              { href: "/bags", label: "Bags" },
              {
                href: "/face-masks-face-coverings",
                label: "Face Masks & Face Coverings",
              },
              { href: "/hats-beanies", label: "Hats & Beanies" },
              {
                href: "/heat-transfer-vinyl",
                label: "Heat Transfer Vinyl",
              },
              { href: "/infants-toddlers", label: "Infants & Toddlers" },
              { href: "/jackets-coats", label: "Jackets & Coats" },
              { href: "/office-apparel", label: "Office Apparel" },
              { href: "/pants", label: "Pants" },
              { href: "/polos", label: "Polos" },
              { href: "/shorts", label: "Shorts" },
              {
                href: "/sweaters-cardigans",
                label: "Sweaters & Cardigans",
              },
              { href: "/sweatshirts", label: "Sweatshirts" },
              {
                href: "/tank-tops-sleeveless-shirts",
                label: "Tank Tops & Sleeveless Shirts",
              },
              { href: "/t-shirts", label: "T-Shirts" },
              { href: "/underwear", label: "Underwear" },
              { href: "/work-shirts", label: "Work Shirts" },
              { href: "/new-arrivals", label: "New Arrivals" },
              { href: "/closeout", label: "Closeout" },
            ].map(({ href, label }) => (
              <MenuItem key={href}>
                <MenuLink href={href}>{label}</MenuLink>
              </MenuItem>
            ))}
          </MenuCategories>
        </NavItem>
        <NavItem isActive={["brands", "all"].includes(menu)}>
          {menu === "all" ? (
            <NavLink
              href="/brands"
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                event.stopPropagation();
                setMenu("brands");
              }}
              isActive={false}
              isDropdown={true}
            >
              Brand
              <DownArrow />
            </NavLink>
          ) : null}
          {resolvedMills ? (
            <MenuBrands isActive={menu === "brands"}>
              {resolvedMills?.map(({ name, logo, path }) => (
                <MenuBrandItem key={name}>
                  <MenuBrandLink href={path}>
                    <MenuBrandLogo
                      src={logo}
                      alt={name}
                    />
                  </MenuBrandLink>
                </MenuBrandItem>
              ))}
              <MenuBrandViewAll />
            </MenuBrands>
          ) : null}
        </NavItem>
        <NavItem isActive={["industries", "all"].includes(menu)}>
          {menu === "all" ? (
            <NavLink
              href="#"
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                event.stopPropagation();
                setMenu("industries");
              }}
              isActive={false}
              isDropdown={true}
            >
              Industry
              <DownArrow />
            </NavLink>
          ) : null}
          <MenuIndustries isActive={menu === "industries"}>
            {[
              {
                href: "/direct-to-garment",
                label: "DTG Direct To Garment",
              },
              { href: "/tie-dye-clothing", label: "Tie-Dye Clothing" },
              { href: "/sublimation", label: "Sublimation" },
              { href: "/screen-printing", label: "Screen Printing" },
              {
                href: "/htv-heat-transfer-vinyl",
                label: "HTV Heat Transfer Vinyl",
              },
              { href: "/embroidery", label: "Embroidery" },
              { href: "/bleaching", label: "Bleaching" },
            ].map(({ href, label }) => (
              <MenuItem key={href}>
                <MenuLink href={href}>{label}</MenuLink>
              </MenuItem>
            ))}
          </MenuIndustries>
        </NavItem>
        <NavItem isActive={menu === "all"}>
          <NavLink href="/sales">
            <span className={`${isDesktop ? "text-red500" : ""}`}>SALE</span>
          </NavLink>
        </NavItem>
        <NavItem isActive={menu === "all"}>
          <NavLink href="/under-5">UNDER $5</NavLink>
        </NavItem>
        <NavItem
          testId="dtf"
          isActive={menu === "all"}
        >
          <NavLink
            onClick={handleDTFNavLinkClick}
            href="https://dtf.shirtspace.com/discount/20OFFDTF"
            target="_blank"
          >
            DTF Transfers
          </NavLink>
        </NavItem>
      </Nav>
    </Container>
  );
};

export default HeaderForStore;
